import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { QuidLibModule } from '@mhe/quid';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import {
  ConfigService,
  launchConfigs,
  ltiParams,
  TranslationConfigService,
  UPModule,
} from 'up';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ErrorComponent } from './shared/components/error/error.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { InterceptorErrorComponent } from './shared/interceptors/interceptor-error/interceptor-error.component';
import { LoaderInterceptor } from './shared/interceptors/loader.interceptor';
import { SetLtiParams } from './shared/store/actions/ltiparams.action';
import { appReducers } from './shared/store/reducers/app.reducers';

export function createTranslateLoader(httpBackend: HttpBackend) {
  return new MultiTranslateHttpLoader(httpBackend, [
    './assets/quid-i18n/',
    './assets/i18n/',
    './assets/up-i18n/',
  ]);
}

export function initializeApplication(
  config: ConfigService,
  store: Store,
  translationConfigService: TranslationConfigService,
  router: Router,
) {
  return (): Promise<void> => {
    const launchConfigs: launchConfigs = {
      createTokenApiUrl: `${environment.apiUrl}/up/jwt/token/create`,
      paramsApiUrl: `${environment.apiUrl}/ebook/params`,
      redirectUrl: '', //'https://www.mheducation.com/highered', TBD
      environmentName: environment.envName,
    };
    return config.processRequest(launchConfigs).then(
      (ltiResponse: ltiParams) => {
        const ltiParams = {
          ltiParams: ltiResponse,
        };
        store.dispatch(new SetLtiParams(ltiParams));
        translationConfigService.setLocale(ltiParams.ltiParams.lmsLocale);
      },
      (error) => {
        translationConfigService.setLocale();
        const unPairBeforeExit =
          !!error?.error?.error?.errorCode?.startsWith('FAIL_IN_PAIR');
        router.navigate(['/error'], {
          state: {
            unPairBeforeExit,
            buttonTranslationKey: unPairBeforeExit
              ? 'INSTRUCTOR.PRODUCT_SELECTION.PAIRING_ERROR_BUTTON_LABEL'
              : null,
            errorMessageLinkLabelKey: unPairBeforeExit
              ? 'INSTRUCTOR.PRODUCT_SELECTION.ERROR_LINK_LABEL'
              : null,
            translateKey: unPairBeforeExit
              ? 'INSTRUCTOR.PRODUCT_SELECTION.PAIRING_ERROR_MESSAGE'
              : null,
            isErrorWithLink: unPairBeforeExit,
          },
        });
      },
    );
  };
}

@NgModule({
  declarations: [
    AppComponent,
    InterceptorErrorComponent,
    HeaderComponent,
    ErrorComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    CommonModule,
    FormsModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend],
      },
    }),
    UPModule,
    NgbModule,
    QuidLibModule,
    StoreModule.forRoot(appReducers),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    { provide: Window, useValue: window },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApplication,
      deps: [ConfigService, Store, TranslationConfigService, Router],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
